/**
 * keybindings.ts — MFix42.7 (2026-06-01) — action registry +
 * binding map for the Controls-tab key rebinding UX.
 *
 * The legacy `input.ts onKeyDown` hardcoded `e.key === 'g'` style
 * branches for every action. This file replaces that with an
 * `ActionId` enum + `DEFAULT_KEYBINDINGS` map + a runtime override
 * field on `ControlsSettings.keyBindings`. The dispatcher in
 * `input.ts` consults `getKeyForAction(settings, action)` to resolve
 * the active binding; the Controls tab in `settings-overlay.ts`
 * captures fresh bindings via a key-capture overlay.
 *
 * Binding key format — `KeyboardEvent.code` strings (layout-
 * independent), optionally prefixed with `'Ctrl+'`, `'Shift+'`, or
 * `'Alt+'` modifiers (in that canonical order). Examples:
 *   - `'KeyG'`         — plain G
 *   - `'Equal'`        — the `=` / `+` physical key
 *   - `'Minus'`        — the `-` / `_` physical key
 *   - `'Space'`
 *   - `'Backquote'`    — `` ` `` / `~`
 *   - `'F9'`
 *   - `'Ctrl+KeyG'`    — Ctrl+G (modifier combo)
 *
 * Use `eventToBindingKey(event)` to compute the binding string from
 * a `KeyboardEvent`; use `bindingMatchesEvent(binding, event)` to
 * test a stored binding against an incoming event (handles modifier
 * canonicalisation and case-insensitive comparison).
 *
 * Reserved keys not exposed for rebinding:
 *   - Escape (cancel / open menu — foundational)
 *   - `'Digit0'..'Digit9'` and `'Ctrl+Digit0'..'Ctrl+Digit9'` (fleet
 *     recall + assign pattern — class-based, not individually
 *     rebindable in v1)
 */

/**
 * All actions the input layer dispatches on. The string values are
 * stable persistent identifiers — store them in save blobs / settings
 * without fear of refactor breakage.
 */
export type ActionId =
  | 'togglePause'
  | 'speedUp'
  | 'speedDown'
  | 'toggleGalaxy'
  | 'toggleCloak'
  | 'overlayProduction'
  | 'overlayThreat'
  | 'overlayExploration'
  | 'overlayDiplomatic'
  | 'devDetectionOverlay'
  | 'devEnemyPov'
  | 'devToolbox';

/**
 * Default key for each action. Matches the pre-MFix42.7 hardcoded
 * bindings — players who never visit the Controls tab see no
 * behavioural change.
 *
 * Some pre-MFix42.7 actions accepted two keys (Equal / `+`,
 * Minus / `_`, KeyG case-insensitive). The new dispatcher reads only
 * the canonical default; the case-insensitive behaviour is preserved
 * by `bindingMatchesEvent` (KeyboardEvent.code is case-insensitive
 * already since 'KeyG' refers to the physical key regardless of
 * Shift state).
 */
export const DEFAULT_KEYBINDINGS: Readonly<Record<ActionId, string>> = Object.freeze({
  togglePause:         'Space',
  speedUp:             'Equal',
  speedDown:           'Minus',
  toggleGalaxy:        'KeyG',
  toggleCloak:         'KeyC',
  overlayProduction:   'KeyP',
  overlayThreat:       'KeyT',
  overlayExploration:  'KeyE',
  overlayDiplomatic:   'KeyD',
  devDetectionOverlay: 'F9',
  devEnemyPov:         'F10',
  devToolbox:          'Backquote',
});

/**
 * Display labels for the Controls tab. Keep short — the rebind UI
 * shows them in a left-column list.
 */
export const ACTION_LABELS: Readonly<Record<ActionId, string>> = Object.freeze({
  togglePause:         'Pause / Resume',
  speedUp:             'Speed Up',
  speedDown:           'Speed Down',
  toggleGalaxy:        'Toggle Galaxy View',
  toggleCloak:         'Toggle Cloak (selected ships)',
  overlayProduction:   'Overlay: Production',
  overlayThreat:       'Overlay: Threat',
  overlayExploration:  'Overlay: Exploration',
  overlayDiplomatic:   'Overlay: Diplomatic',
  devDetectionOverlay: 'Dev: Detection Overlay',
  devEnemyPov:         'Dev: Enemy POV',
  devToolbox:          'Dev: Toolbox',
});

/**
 * Canonical action-id list in the display order the Controls tab
 * should use. Game-control actions first, then dev tools at the
 * bottom so they're visible but de-emphasised.
 */
export const ACTION_IDS_IN_DISPLAY_ORDER: readonly ActionId[] = [
  'togglePause',
  'speedUp',
  'speedDown',
  'toggleGalaxy',
  'toggleCloak',
  'overlayProduction',
  'overlayThreat',
  'overlayExploration',
  'overlayDiplomatic',
  'devDetectionOverlay',
  'devEnemyPov',
  'devToolbox',
];

/**
 * Resolve the active binding string for an action — consults the
 * empire's saved override map first, falls back to the default.
 * Pure; no DOM dependencies.
 */
export function getKeyForAction(
  overrides: Partial<Record<ActionId, string>> | undefined,
  action: ActionId,
): string {
  return overrides?.[action] ?? DEFAULT_KEYBINDINGS[action];
}

/**
 * Build a canonical binding-key string from a KeyboardEvent.
 * Modifier prefix order is fixed (Ctrl > Shift > Alt) so two events
 * that fire the same combo always produce the same string regardless
 * of which modifier was depressed first.
 *
 * Returns null when:
 *   - The event carries no `.code` (rare — synthetic events)
 *   - The base key is itself a modifier (ControlLeft / ShiftRight /
 *     AltGraph etc.) — modifier-only bindings aren't useful
 *
 * Use this both in the capture overlay (to compute the binding to
 * store) and indirectly via `bindingMatchesEvent` (to match incoming
 * events against stored bindings).
 */
export function eventToBindingKey(event: KeyboardEvent): string | null {
  const code = event.code;
  if (!code) return null;
  if (isModifierCode(code)) return null;
  let out = code;
  if (event.altKey) out = `Alt+${out}`;
  if (event.shiftKey) out = `Shift+${out}`;
  if (event.ctrlKey || event.metaKey) out = `Ctrl+${out}`;
  return out;
}

/**
 * Test whether a stored binding matches an incoming KeyboardEvent.
 * Wraps `eventToBindingKey` + string compare so the dispatcher reads
 * naturally:
 *
 *   if (bindingMatchesEvent(getKeyForAction(s, 'toggleCloak'), e)) {
 *     ...
 *   }
 */
export function bindingMatchesEvent(
  binding: string,
  event: KeyboardEvent,
): boolean {
  const eventKey = eventToBindingKey(event);
  if (eventKey === null) return false;
  return eventKey === binding;
}

/**
 * Friendly display label for a binding-key string. Used by the
 * Controls UI to render bindings as e.g. "Space", "G", "Ctrl+Shift+F9"
 * instead of the raw KeyboardEvent.code values.
 */
export function displayLabelForBinding(binding: string): string {
  // Split out modifier prefix(es).
  const parts = binding.split('+');
  const base = parts[parts.length - 1]!;
  const modifiers = parts.slice(0, -1);
  return [...modifiers, prettifyKeyCode(base)].join('+');
}

/**
 * Map raw `KeyboardEvent.code` values to player-friendly labels.
 * Falls through to the raw code if unrecognised — that keeps the
 * function safe across keyboard layouts we haven't catalogued.
 */
function prettifyKeyCode(code: string): string {
  // KeyA..KeyZ → A..Z
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  // Digit0..Digit9 → 0..9
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  // Numpad0..Numpad9 → Num 0..Num 9
  if (code.startsWith('Numpad') && code.length === 7) return `Num ${code.slice(6)}`;
  // F-keys pass through unchanged (F1..F12 already readable).
  if (/^F\d+$/.test(code)) return code;
  // Common punctuation.
  switch (code) {
    case 'Equal':         return '=';
    case 'Minus':         return '-';
    case 'Backquote':     return '`';
    case 'BracketLeft':   return '[';
    case 'BracketRight':  return ']';
    case 'Semicolon':     return ';';
    case 'Quote':         return "'";
    case 'Backslash':     return '\\';
    case 'Comma':         return ',';
    case 'Period':        return '.';
    case 'Slash':         return '/';
    case 'Space':         return 'Space';
    case 'Tab':           return 'Tab';
    case 'Enter':         return 'Enter';
    case 'Backspace':     return 'Backspace';
    case 'ArrowUp':       return '↑';
    case 'ArrowDown':     return '↓';
    case 'ArrowLeft':     return '←';
    case 'ArrowRight':    return '→';
    case 'Home':          return 'Home';
    case 'End':           return 'End';
    case 'PageUp':        return 'PgUp';
    case 'PageDown':      return 'PgDn';
    case 'Insert':        return 'Insert';
    case 'Delete':        return 'Del';
    default:              return code;
  }
}

/**
 * Set of modifier-key codes. Used by `eventToBindingKey` to reject
 * modifier-only captures (a bare Shift press shouldn't bind to
 * anything).
 */
function isModifierCode(code: string): boolean {
  return (
    code === 'ControlLeft' ||
    code === 'ControlRight' ||
    code === 'ShiftLeft' ||
    code === 'ShiftRight' ||
    code === 'AltLeft' ||
    code === 'AltRight' ||
    code === 'MetaLeft' ||
    code === 'MetaRight' ||
    code === 'AltGraph' ||
    code === 'CapsLock'
  );
}

/**
 * Conflict detection — returns the action(s) currently bound to the
 * given binding string, excluding `selfAction` (the action being
 * rebound; comparing against itself isn't a conflict).
 *
 * Empty array = no conflict. Single entry = standard "this key is
 * already bound to X" warning. Multiple entries shouldn't occur in
 * normal use (the rebind UI prevents creating duplicates) but the
 * function returns all matches for defensive UI rendering.
 */
export function actionsBoundTo(
  overrides: Partial<Record<ActionId, string>> | undefined,
  binding: string,
  selfAction: ActionId,
): ActionId[] {
  const out: ActionId[] = [];
  for (const action of ACTION_IDS_IN_DISPLAY_ORDER) {
    if (action === selfAction) continue;
    if (getKeyForAction(overrides, action) === binding) out.push(action);
  }
  return out;
}

/**
 * Reserved binding strings the rebind UI must reject. Escape is
 * foundational (cancel / open menu). Tab / Enter / Backspace are
 * browser-essential. Modifier-only bindings are rejected at
 * `eventToBindingKey` time but listed here for completeness.
 */
export const RESERVED_BINDINGS: ReadonlySet<string> = new Set([
  'Escape',
  'Tab',
  'Enter',
  'Backspace',
  // Modifier-only — already rejected at capture, listed for clarity.
  'ControlLeft', 'ControlRight',
  'ShiftLeft', 'ShiftRight',
  'AltLeft', 'AltRight',
  'MetaLeft', 'MetaRight',
]);

export function isReservedBinding(binding: string): boolean {
  // A modifier combo (e.g. Ctrl+KeyA) is allowed even if the base
  // key alone (KeyA) would be reserved. Only check the bare binding.
  if (binding.includes('+')) return false;
  return RESERVED_BINDINGS.has(binding);
}
